import { Layout } from "@jambushrushti/components/layout/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Heart,
  Scroll,
  BookOpen,
  GraduationCap,
  Star,
} from "lucide-react";

const parivarMembers = [
  {
    name: "Pujya Muni Shri Namaskarvijayji Maharaj",
    role: "Beloved Disciple",
    description:
      "A devoted disciple who served Gurudev with unwavering dedication and continued his legacy of scholarship.",
    image:
      "https://siddhijambuparivar.com/wp-content/uploads/2022/11/101-min.jpg",
  },
  {
    name: "Pujya Muni Shri Purnarakshitvijayji Maharaj",
    role: "Spiritual Successor",
    description:
      "Carrying forward Gurudev's mission of preserving Jain scriptures and spreading spiritual knowledge.",
    image:
      "https://siddhijambuparivar.com/wp-content/uploads/2022/11/107-min.jpg",
  },
  {
    name: "Pujya Muni Shri Siddhisenvijayji Maharaj",
    role: "Scholar-Monk",
    description:
      "Dedicated to the study and propagation of Jain Agamas following Gurudev's scholarly traditions.",
    image:
      "https://siddhijambuparivar.com/wp-content/uploads/2022/11/99-min.jpg",
  },
];

const contributions = [
  {
    icon: BookOpen,
    title: "Manuscript Preservation",
    description:
      "Continuing the work of preserving and cataloging ancient Jain manuscripts across India",
  },
  {
    icon: GraduationCap,
    title: "Scholarly Training",
    description:
      "Training new generations of monks in Sanskrit, Prakrit, and Jain philosophy",
  },
  {
    icon: Scroll,
    title: "Publication Work",
    description:
      "Editing and publishing critical editions of Jain scriptures following Gurudev's methodology",
  },
  {
    icon: Users,
    title: "Community Outreach",
    description:
      "Conducting lectures, workshops, and spiritual discourses for the community",
  },
];

const GurudevParivar = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-5 sm:py-8 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="animate-fade-up mx-auto max-w-3xl text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary/80 px-3 py-1.5 text-xs sm:mb-6 sm:px-4 sm:py-2 sm:text-sm">
              <Users className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
              <span className="text-muted-foreground">Spiritual Lineage</span>
            </div>
            <h1 className="font-display mb-3 text-3xl font-bold text-foreground sm:mb-6 sm:text-5xl">
              Legacy of Pujyapad Muni Jambuvijayaji Maharaj
            </h1>
            <p className="text-base text-muted-foreground sm:text-lg">
              The disciples and spiritual successors of Gurudev Muni
              Jambuvijayji Maharaj Saheb, continuing his legacy of scholarship,
              devotion, and service to Jain philosophy.
            </p>
          </div>
        </div>
      </section>

      {/* Lineage Introduction */}
      <section className="bg-background py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <div className="grid items-center gap-8 md:grid-cols-2 lg:gap-12">
              <div className="animate-fade-up">
                <h2 className="font-display mb-4 text-2xl font-bold text-foreground sm:mb-6 sm:text-3xl">
                  A Legacy of <span className="text-primary">Devotion</span>
                </h2>
                <p className="mb-4 text-muted-foreground">
                  Gurudev Muni Jambuvijayji Maharaj Saheb's spiritual lineage
                  continues through his devoted disciples who carry forward his
                  mission of preserving and propagating Jain knowledge.
                </p>
                <p className="mb-6 text-muted-foreground">
                  Each member of the Parivar has dedicated their life to the
                  study of scriptures, service to the community, and the
                  preservation of our ancient wisdom traditions.
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3">
                    {parivarMembers.slice(0, 3).map((member, i) => (
                      <div
                        key={i}
                        className="h-12 w-12 overflow-hidden rounded-full border-2 border-background"
                      >
                        <img
                          src={member.image}
                          alt={member.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Disciples continuing Gurudev's mission
                  </span>
                </div>
              </div>
              <div className="animate-fade-up relative delay-200">
                <div className="shadow-elevated aspect-square overflow-hidden rounded-2xl">
                  <img
                    src="https://siddhijambuparivar.com/wp-content/uploads/2022/11/107-min.jpg"
                    alt="Legacy of Pujyapad Muni Jambuvijayaji Maharaj"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 flex h-24 w-24 items-center justify-center rounded-2xl bg-primary/10">
                  <Heart className="h-10 w-10 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parivar Members */}
      <section className="bg-gradient-spiritual py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-6 text-center sm:mb-8">
            <h2 className="font-display mb-3 text-2xl font-bold text-foreground sm:mb-4 sm:text-3xl">
              Members of the Parivar
            </h2>
            <p className="mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base">
              Distinguished monks who received their spiritual training and
              guidance from Gurudev
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            {parivarMembers.map((member, index) => (
              <Card
                key={member.name}
                variant="feature"
                className="animate-fade-up overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="mb-2 flex items-center gap-2">
                    <Star className="h-4 w-4 text-gold" />
                    <span className="text-sm font-medium text-primary">
                      {member.role}
                    </span>
                  </div>
                  <h3 className="font-display mb-2 text-lg font-semibold text-foreground">
                    {member.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {member.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contributions */}
      <section className="bg-background py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-6 text-center sm:mb-8">
            <h2 className="font-display mb-3 text-2xl font-bold text-foreground sm:mb-4 sm:text-3xl">
              Continuing Gurudev's Mission
            </h2>
            <p className="mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base">
              The Parivar actively contributes to preserving and spreading Jain
              knowledge
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {contributions.map((item, index) => (
              <Card
                key={item.title}
                variant="interactive"
                className="animate-fade-up text-center"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                    <item.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-display mb-2 font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-hero lotus-pattern py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display mb-4 text-2xl font-bold text-foreground">
            Learn More About Our Spiritual Heritage
          </h2>
          <p className="mx-auto mb-6 max-w-xl text-muted-foreground">
            Explore Gurudev's life, teachings, and the Muni Jambuvijayaji
            Research Center dedicated to preserving his legacy
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="hero" asChild>
              <Link to="/about/gurudev">
                Gurudev's Biography
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/about/gyan-kendra">
                Visit Research Center
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GurudevParivar;
